/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 3 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import SelectDropDown, { type SelectEventArgs } from "@CubeComponents/Mui_Form/SelectDropDown";
import IconComponent from "@CubeComponents/icons";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import { useEffect, useMemo, useRef, useState } from "react";
import { getChargesList } from "@Actions/Charges";
import { useParams } from "react-router-dom";
import CostDatabases from "@Features/portal-settings/components/PortalSetings/Estimates/CostDatabases/CostDatabases";
import { CostDatabasePage as CostDatabase } from "@Features/estimation";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import {
  ESTIMATE_LEVEL_MEASUREMENT_UNIT,
  getEstimateNumberValueBasedOnSettings,
  isEstimateRequiredPermission,
  rateAnalysisItemType,
} from "@Actions/estimation";
import { numToChar, toRoman } from "@Services/utils/helpers";
import { v4 as uuid } from "uuid";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import axiosWrapper from "@AxiosWrapper";
import { portalPurchaseUnitsUrl } from "@Services/dataManager";
import type { GetPurchaseUnitsResponse } from "@ServerTypes/units/units.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import { TOAST_TYPE_ERROR, TOAST_TYPE_WARNING } from "@Services/constants";
import { useDispatch, useSelector } from "react-redux";
import { showToastNotification } from "@Reducers/toastSlice";
import Attachments from "./RateAnalysisAttachments";
import { folderPermissions } from "@Helpers/constants";
import _ from "lodash";
import { useRateAnalysisControlledState } from "./RateAnalysisControlledState";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import Text from "@CubeComponents/html/Text/Text";
import type { RootState } from "src/reducers";

const { RESOURCE_MODIFY } = folderPermissions();

type RateAnalysisProps = {
  rateAnalysisItemsData: {
    parentRecord?: { code?: string; description?: string };
    checkedRecord?: {
      code?: string;
      description?: string;
      purchaseUnit?: { name?: string };
      purchaseUnitName?: string;
    };
  };
  saveRateAnalysisForItem?: (args: { data: RateAnalysisPayload }) => Promise<void> | void;
  handleCloseRateAnalysisModal?: () => void;
  rateAnalysis?: RateAnalysisPayload;
  viewMode?: boolean;
  source?: string;
  currencyData?: { id?: string; selectedSymbol?: { name?: string } };
  itemDropdownId?: string;
  chargeDropdownId?: string;
  showAnalysisOnly?: boolean;
  parentRateAnalysis?: RateAnalysisData;
  setParentRateAnalysis?: (data: RateAnalysisData) => void;
  parentRateAnalysisUnitCount?: number;
  setParentRateAnalysisUnitCount?: (value: number) => void;
};

/**
 * Rate-analysis file attachment shape. Stored inside the `rateAnalysisData.data` array
 * (which is `z.any()` in the collection's `rateAnalysisObj` for legacy compatibility),
 * so no Zod equivalent exists. Exported for reuse by `RateAnalysisAttachments.tsx`.
 */
export type Attachment = {
  id?: string;
  name: string;
  size: number;
  type?: string;
  fileKey?: string;
  fileBase64Str?: string;
  modifiedDate?: number;
  modifiedBy?: {
    fullName?: string;
  };
};

export type RateAnalysisRow = {
  id: string | null;
  type?: RateAnalysisItemType;
  serial?: number;
  amount?: number | null;
  code?: string | null;
  uniqueCode?: string | number | null;
  description?: string;
  purchaseUnitsId?: string | null;
  unit?: string;
  rate?: number | null;
  quantity?: number | null;
  selectedItems?: string[];
  costDatabaseName?: string;
  costDatabaseId?: string | null;
  versionId?: string | null;
  chargeId?: string;
  attachments?: Attachment[];
  purchaseUnitCount?: number;
  isTypeChanged?: boolean;
  prevItemId?: string;
  /** Display text used by the charge-items autocomplete dataset (frontend-only). */
  text?: string;
};

/**
 * Frontend's tight typing of the rate-analysis `data` block. Mirrors the
 * `rateAnalysisData` field in the collection's `EstimateItemRateAnalysis`
 * (`packages/server/estimates/estimates.collection.ts`), but narrows `data` from
 * `any[]` (intentionally loose in the schema to accept legacy formats) to typed
 * `RateAnalysisRow[]`.
 */
export type RateAnalysisData = {
  data: RateAnalysisRow[];
  total: number;
  say: number;
};

type RateAnalysisItemType = (typeof rateAnalysisItemType)[keyof typeof rateAnalysisItemType];

type PrepareRateAnalysisArgs =
  | { action: "add"; type: RateAnalysisItemType; data: RateAnalysisRow }
  | { action: "edit"; data: RateAnalysisRow & { isTypeChanged?: boolean; prevItemId?: string } }
  | { action: "delete"; data: string[] };

/**
 * A cost-database row as it comes out of the CostDatabases grid. That component is typed
 * `props: any` and hands its selection callback untyped rows, so this local type is their single
 * declaration site; `_id` is the CostDatabase _id (24-hex ObjectId per costDatabase.schema.ts).
 */
type CostDatabaseRecord = {
  _id: ObjectIdString;
  name: string;
};

type CostDatabaseItemRecord = {
  _id: string;
  description: string;
  rate?: number;
  purchaseUnit?: { id?: string; name?: string };
  purchaseUnitCount?: number;
  code?: string;
  uniqueCode?: string;
  costDatabaseId?: string;
  versionId?: string;
  costDatabaseName?: string;
  rateAnalysis?: RateAnalysisPayload;
};

type SlickGridHandle = {
  slickGrid: {
    getSelectedRows: () => number[];
    getDataItem: (row: number) => RateAnalysisRow;
    selectedRows?: number[];
    setSelectedRows?: (rows: number[]) => void;
  };
  dataView?: {
    items: RateAnalysisRow[];
    setItems?: (items: RateAnalysisRow[]) => void;
  };
  refresh?: () => void;
};

export type RateAnalysisPayload = {
  purchaseUnitCount: number;
  rateAnalysisData: RateAnalysisData;
};

// selectedChargeIdsRef tracks multi-selected charge item IDs from TreeSelectDropDown

function RateAnalysis({
  rateAnalysisItemsData,
  saveRateAnalysisForItem,
  handleCloseRateAnalysisModal,
  rateAnalysis,
  viewMode,
  source,
  // isSubAOR = false,
  currencyData,
  itemDropdownId = "item",
  chargeDropdownId = "charge",
  showAnalysisOnly,
  parentRateAnalysis,
  setParentRateAnalysis,
  parentRateAnalysisUnitCount,
  setParentRateAnalysisUnitCount,
}: RateAnalysisProps) {
  const { t } = useTranslation();
  const { parentRecord, checkedRecord } = rateAnalysisItemsData;

  const purchaseUnitName = checkedRecord?.purchaseUnit?.name ?? checkedRecord?.purchaseUnitName ?? "";

  const [purchaseUnitCount, setPurchaseUnitCount] = useRateAnalysisControlledState(
    parentRateAnalysisUnitCount,
    setParentRateAnalysisUnitCount,
    1,
  );
  const [chargesItems, setChargesItems] = useState<Array<{ id: string; text: string; percentage: number }> | null>(
    null,
  );
  const [openCostDatabaseModal, setOpenCostDatabaseModal] = useState(false);
  const [openMarketRateDatabaseModal, setOpenMarketRateDatabaseModal] = useState(false);
  const [selectedCostDatabaseId, setSelectedCostDatabaseId] = useState<CostDatabaseRecord | null>(null);
  const [checkedCostDatabaseIds, setCheckedCostDatabaseIds] = useState<CostDatabaseRecord[]>([]);
  const [checkedCostDatabaseItemIds, setCheckedCostDatabaseItemIds] = useState<CostDatabaseItemRecord[]>([]);
  const [rateAnalysisData, setRateAnalysisData] = useRateAnalysisControlledState<RateAnalysisData>(
    parentRateAnalysis,
    setParentRateAnalysis,
    {
      data: [],
      total: 0,
      say: 0,
    },
  );

  const [importFromCostDatabase, setImportFromCostDatabase] = useState(false);
  const [checkedMarketrateItemIds, setCheckedMarketrateItemIds] = useState<CostDatabaseItemRecord[]>([]);
  const [importFrommarketRateDatabase, setImportFrommarketRateDatabase] = useState(false);
  const [showConfirmImportModal, setShowConfirmImportModal] = useState(false);
  const [openAttachmentModal, setOpenAttachmentModal] = useState<false | { rowId: string; attachments?: Attachment[] }>(
    false,
  );
  // const [openSubAOR, setOpenSubAOR] = useState({ show: false, data: {} });
  const [isUserPerformedAction, setIsUserPerformedAction] = useState(false);
  const [isGridInEditMode, setIsGridInEditMode] = useState(false);
  const estimateData = useSelector((state: RootState) => state.estimateData) as { userRole?: number };
  const actionPermissionAllowed =
    source === ESTIMATE_LEVEL_MEASUREMENT_UNIT ? isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id) : true;

  const chargesRef = useRef<Array<{ id: string; text: string; percentage: number }> | null>(null);
  const rateAnalysisDataRef = useRef<RateAnalysisData>(rateAnalysisData);
  const purchaseUnitCountRef = useRef(purchaseUnitCount);
  const [purchaseUnitsOptions, setPurchaseUnitsOptions] = useState<NonNullable<GetPurchaseUnitsResponse["result"]>>([]);
  const addedChargeRef = useRef<{ id: string; text?: string; percentage?: number } | null>(null);
  const isTypeChangedRef = useRef<boolean | null>(null);
  const selectedRecordRef = useRef<RateAnalysisRow | null>(null);
  const isItemsPage = useRef(false);
  const params = useParams();

  const [toolbarButtonsDisabled, setToolbarButtonsDisabled] = useState({
    edit: true,
    delete: true,
    changeType: true,
    update: true,
    cancel: true,
    [itemDropdownId]: viewMode || !actionPermissionAllowed ? true : false,
    [chargeDropdownId]: true,
  });

  const [editingRowValue, setEditingRowValue] = useState<Partial<RateAnalysisRow>>({});

  const editingRowValueRef = useRef(editingRowValue);

  useEffect(() => {
    editingRowValueRef.current = editingRowValue;
  }, [editingRowValue]);

  const dispatch = useDispatch();

  const chargesInit = async () => {
    const response = await getChargesList();
    if (response.data.status) {
      const result = (response.data.result ?? []) as Array<{
        name?: string;
        _id: string;
        percentage?: string | number;
      }>;
      const data = result.map((charge) => ({
        text: charge.name ?? "",
        id: charge._id,
        percentage: parseFloat(String(charge.percentage ?? 0)),
      }));
      setChargesItems(data);
    }
  };

  useEffect(() => {
    chargesInit();
    if (rateAnalysis) {
      setPurchaseUnitCount(rateAnalysis.purchaseUnitCount ?? 1);
      setRateAnalysisData(_.cloneDeep(rateAnalysis.rateAnalysisData));
      if (rateAnalysis.rateAnalysisData.data.length > 0 && !viewMode && actionPermissionAllowed) {
        toolbarButtonStateManager({
          [chargeDropdownId]: false,
        });
      }
    } else if (parentRateAnalysis && parentRateAnalysis.data.length > 0 && !viewMode && actionPermissionAllowed) {
      toolbarButtonStateManager({
        [chargeDropdownId]: false,
      });
    }
  }, []);

  useEffect(() => {
    if (chargesItems) {
      chargesRef.current = chargesItems;
    }
  }, [chargesItems]);

  useEffect(() => {
    rateAnalysisDataRef.current = rateAnalysisData;
  }, [rateAnalysisData]);

  useEffect(() => {
    purchaseUnitCountRef.current = purchaseUnitCount;
  }, [purchaseUnitCount]);

  useEffect(() => {
    const fetchPurchaseUnits = async () => {
      const response = await axiosWrapper.post<GetPurchaseUnitsResponse>(portalPurchaseUnitsUrl, {
        value: { params: {} },
      });
      if (response.data?.result) {
        setPurchaseUnitsOptions(response.data.result);
      }
    };
    fetchPurchaseUnits();
  }, []);

  const dataGrid = useRef<SlickGridHandle | null>(null);
  const itemDropDownButtonItems = [
    { text: "Cost Database Item", id: "costDatabaseItem" },
    { text: "Manual Item", id: "manualItem" },
  ];
  // const importDropdownItems = [{ text: "From CostDatabase", id: "costDatabase" }];
  // if(source !== itemListSources.COST_DATABASE){
  //   itemDropDownButtonItems.splice(1,0,{text: 'Market Rate Item', id: 'marketrateItem'})
  // }
  const handleitemSelectionHandler = (args: SelectEventArgs) => {
    if (!actionPermissionAllowed) {
      showToast(t("You Don't Have Required Permission to Perform this Action."), TOAST_TYPE_ERROR);
      return;
    }
    const selectedRecordsData = dataGrid.current?.slickGrid
      .getSelectedRows()
      .map((data: number) => dataGrid.current?.slickGrid.getDataItem(data))
      .filter(Boolean) as RateAnalysisRow[];
    const selectedRecord = selectedRecordsData[0];
    const selectedType = selectedRecord?.type;
    if (
      selectedType &&
      [rateAnalysisItemType.MANUAL, rateAnalysisItemType.COST_DATABASE, rateAnalysisItemType.MARKETRATE].includes(
        selectedType,
      )
    ) {
      selectedRecordRef.current = selectedRecord;
    } else {
      selectedRecordRef.current = null;
    }
    if (args.item.id == "costDatabaseItem") {
      setOpenCostDatabaseModal(true);
    } else if (args.item.id == "manualItem") {
      setIsGridInEditMode(true);
      toolbarButtonStateManager({
        update: false,
        cancel: false,
      });
      actionBegin({ type: "actionBegin", requestType: "add" });
    } else if (args.item.id === "marketrateItem") {
      setOpenMarketRateDatabaseModal(true);
    }
  };

  const handleChargeSelectionHandler = (args: {
    item: { properties: { id?: string; text?: string; percentage?: number }; percentage?: number };
  }) => {
    const selectedRecordsData = dataGrid.current?.slickGrid
      .getSelectedRows()
      .map((data: number) => dataGrid.current?.slickGrid.getDataItem(data))
      .filter(Boolean) as RateAnalysisRow[];
    const selectedRecord = selectedRecordsData[0];
    if (selectedRecord?.type && [rateAnalysisItemType.SUB_TOTAL].includes(selectedRecord.type)) {
      selectedRecordRef.current = selectedRecord;
    } else {
      selectedRecordRef.current = null;
    }
    const chargeProperties = args.item.properties;
    addedChargeRef.current = {
      id: chargeProperties.id ?? "",
      text: chargeProperties.text,
      percentage: args.item.percentage ?? chargeProperties.percentage,
    };
    setIsGridInEditMode(true);
    toolbarButtonStateManager({
      update: false,
      cancel: false,
    });
    actionBegin({ type: "actionBegin", requestType: "add" });
  };

  // const handleImportSelection = (args) => {
  //   if (args.item.id === "costDatabase") {
  //     setImportFromCostDatabase(true);
  //   } else if (args.item.id == "marketRateDatabase") {
  //     setImportFrommarketRateDatabase(true);
  //   }
  // };

  const itemButtonTemplate = () => (
    <div>
      <SelectDropDown
        allowButtonPadding={false}
        buttonType="dropdownBackgroundButton"
        select={handleitemSelectionHandler}
        items={itemDropDownButtonItems}
        id={itemDropdownId}
      >
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
          <div className="flex items-center gap-1">
            <IconComponent name="add" width="1rem" height="1rem" />
            <Text>{t("Item")}</Text>
          </div>
        </ButtonComponent>
      </SelectDropDown>
    </div>
  );

  const chargeButtonTemplate = () => (
    <div>
      <SelectDropDown
        buttonType="dropdownBackgroundButton"
        allowButtonPadding={false}
        select={handleChargeSelectionHandler}
        items={chargesRef.current ?? undefined}
        id={chargeDropdownId}
      >
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
          <div className="flex items-center gap-1">
            <IconComponent name="add" width="1rem" height="1rem" />
            <Text>{t("Charge")}</Text>
          </div>
        </ButtonComponent>
      </SelectDropDown>
    </div>
  );

  const handleChangeTypeButtonClick = () => {
    const selectedRecords = dataGrid.current?.slickGrid
      .getSelectedRows()
      .map((data: number) => dataGrid.current?.slickGrid.getDataItem(data))
      .filter(Boolean) as RateAnalysisRow[];
    const selectedType = selectedRecords[0]?.type;
    if (
      selectedType &&
      selectedRecords.length === 1 &&
      [rateAnalysisItemType.COST_DATABASE, rateAnalysisItemType.MARKETRATE].includes(selectedType)
    ) {
      isTypeChangedRef.current = true;
      actionBegin({ requestType: "beginEdit", type: "actionBegin", rowData: selectedRecords });
    }
  };

  const changeTypeButtonTemplate = () => (
    <div className="me-7">
      <ButtonComponent
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        id="changeType"
        onClick={handleChangeTypeButtonClick}
      >
        <span className="flex items-center gap-1">
          <IconComponent name="changeType" width="1rem" height="1rem" />
          <Text>{t("Change Type")}</Text>
        </span>
      </ButtonComponent>
    </div>
  );

  const handleUpdateButtonClick = () => {
    const selectedRowIndex = dataGrid.current?.slickGrid.selectedRows?.[0];
    const selectedRowData =
      selectedRowIndex !== undefined ? { ...dataGrid.current?.dataView?.items[selectedRowIndex] } : undefined;
    const newData = Object.assign(selectedRowData ?? {}, editingRowValueRef.current) as RateAnalysisRow;
    if (newData.id === null) {
      actionBegin({ action: "add", requestType: "save", data: newData });
    } else {
      actionBegin({ action: "edit", requestType: "save", data: newData });
    }
  };

  const updateButtonTemplate = () => (
    <div className="me-7">
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} id="update" onClick={handleUpdateButtonClick}>
        <span className="flex items-center gap-1">
          <IconComponent name="Update" width="1rem" height="1rem" />
          <Text>{t("Update")}</Text>
        </span>
      </ButtonComponent>
    </div>
  );

  const cancelButtonTemplate = () => (
    <div className="me-7">
      <ButtonComponent
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        id="cancel"
        onClick={() => {
          actionBegin({ action: "cancel", requestType: "cancel" });
        }}
      >
        <span className="flex items-center gap-1">
          <IconComponent name="close" width="0.75rem" height="0.75rem" />
          <Text>{t("Cancel")}</Text>
        </span>
      </ButtonComponent>
    </div>
  );

  const toolbarOptions = useMemo(
    () => [
      {
        align: "Left" as const,
        template: itemButtonTemplate,
        id: itemDropdownId,
        disabled: Boolean(toolbarButtonsDisabled[itemDropdownId]),
      },
      {
        align: "Left" as const,
        template: chargeButtonTemplate,
        id: chargeDropdownId,
        disabled: Boolean(toolbarButtonsDisabled[chargeDropdownId]),
      },
      {
        id: "edit",
        prefixIcon: "editProfile",
        name: t("Edit"),
        align: "Left" as const,
        disabled: toolbarButtonsDisabled.edit,
      },
      {
        id: "delete",
        prefixIcon: "delete",
        name: t("Delete"),
        align: "Left" as const,
        disabled: toolbarButtonsDisabled.delete,
      },
      {
        align: "Left" as const,
        template: changeTypeButtonTemplate,
        id: "changeType",
        disabled: toolbarButtonsDisabled.changeType,
      },
      {
        align: "Left" as const,
        template: updateButtonTemplate,
        id: "update",
        disabled: toolbarButtonsDisabled.update,
      },
      {
        align: "Left" as const,
        template: cancelButtonTemplate,
        id: "cancel",
        disabled: toolbarButtonsDisabled.cancel,
      },
    ],

    [toolbarButtonsDisabled, itemDropdownId, chargeDropdownId],
  );

  const handleSaveClickHandler = () => {
    saveRateAnalysisForItem?.({
      data: {
        rateAnalysisData,
        purchaseUnitCount,
      },
    });
  };

  const closeAttachmentModal = () => {
    setOpenAttachmentModal(false);
  };

  const handleAttachFiles = (attachments: Attachment[]) => {
    setIsUserPerformedAction(true);
    if (!openAttachmentModal) return;
    const updatedData = rateAnalysisDataRef.current.data.map((ele: RateAnalysisRow) =>
      ele.id === openAttachmentModal.rowId ? { ...ele, attachments } : ele,
    );
    setRateAnalysisData({ ...rateAnalysisDataRef.current, data: updatedData });
    dataGrid.current?.refresh?.();
    closeAttachmentModal();
  };

  const attachmentClickHandler =
    (params: { id?: string | null; attachments?: Attachment[] }, disabled: boolean) =>
    (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      if (disabled || !params.id) return;
      const rowData = rateAnalysisDataRef.current.data.find((row: RateAnalysisRow) => row.id == params.id);
      setOpenAttachmentModal({
        rowId: params.id,
        attachments: rowData?.attachments,
      });
    };

  const toolbarClickHandler = (toolClicked: string) => {
    const selectedRecords = dataGrid.current?.slickGrid
      .getSelectedRows()
      .map((data: number) => dataGrid.current?.slickGrid.getDataItem(data))
      .filter(Boolean) as RateAnalysisRow[];

    if (toolClicked === "edit" && selectedRecords.length == 1) {
      actionBegin({ requestType: "beginEdit", type: "actionBegin", rowData: selectedRecords });
    } else if (toolClicked == "delete" && selectedRecords.length) {
      handleRecordsDelete(selectedRecords);
    }
  };

  // function handleCloseSubAOR() {
  //   // setOpenSubAOR({ show: false, data: {} });
  // }

  // async function handleSaveSubAOR({ data  }: any) {
  //   const rate = data.rateAnalysisData.say;
  //   const {
  //     description,
  //     purchaseUnitsId,
  //     purchaseUnit,
  //     quantity,
  //     id: prevItemId,
  //   } = openSubAOR.data;
  //   const params = {
  //     action: "insert",
  //     value: {
  //       description,
  //       purchaseUnit: purchaseUnitsId,
  //       rate,
  //       rateAnalysis: data,
  //       currency: currencyData.id,
  //       isReturnData: true,
  //     },
  //   };
  //   const saveResponse = await getMarketRateItems(params);
  //   if (saveResponse.data.status) {
  //     showToast("Sub-AOR created successfully!", TOAST_TYPE_SUCCESS);
  //     const { itemId, uniqueCode, costDatabaseId, versionId } = saveResponse.data.data;
  //     dataGrid.current.closeEdit();
  //     const prepareData = {
  //       id: itemId,
  //       uniqueCode,
  //       costDatabaseId,
  //       versionId,
  //       description,
  //       purchaseUnitsId,
  //       unit: purchaseUnit,
  //       rate,
  //       quantity,
  //       amount: quantity && rate ? quantity * rate : 0,
  //       type: rateAnalysisItemType.MARKETRATE,
  //     };
  //     if (prevItemId) {
  //       prepareData.prevItemId = prevItemId;
  //     }
  //     prepareRateAnalysisData({
  //       data: prepareData,
  //       action: prevItemId ? "edit" : "add",
  //       type: rateAnalysisItemType.MARKETRATE,
  //     });
  //   }
  //   handleCloseSubAOR();
  // }

  // function subAorClickHandler(e) {
  //   e.stopPropagation();
  //   const listArray = [...dataGrid.current.editModule.formObj.inputElements];
  //   const description = listArray.find((ele: any) => ele.name === "description").ej2_instances[0].value;
  //   const purchaseUnitsId = listArray.find((ele: any) => ele.id === "unit").ej2_instances[0].value;
  //   let quantity = listArray.find((ele: any) => ele.name === "quantity").value;
  //   quantity = Number(quantity);

  //   const data = {
  //     description,
  //     purchaseUnitsId,
  //     purchaseUnit: purchaseUnitDropdownRef.current.itemData?.name,
  //     quantity: quantity && +quantity,
  //   };
  //   if (dataGrid.current.editModule.editModule.args.rowData.id) {
  //     data.id = dataGrid.current.editModule.editModule.args.rowData.id;
  //   }

  //   if (!description || !purchaseUnitsId) {
  //     showToast("Description and unit of item is mandatory!", TOAST_TYPE_ERROR);
  //   }
  //   //  else {
  //   //   setOpenSubAOR({ show: true, data });
  //   // }
  // }

  function rateEditorTemplate(params: { updatedColumn: { field: string } }) {
    const { code, rate, type, uniqueCode } = editingRowValue;

    if (type === rateAnalysisItemType.ADDITIONAL_CHARGE) {
      return "-";
    }
    return (
      <div className="flex gap-1">
        {numericComponent({
          value: rate,
          disabled: Boolean(code || uniqueCode),
          id: params.updatedColumn.field,
          name: params.updatedColumn.field,
          min: 0,
        })}
        {/* {![rateAnalysisItemType.COST_DATABASE, rateAnalysisItemType.MARKETRATE].includes(type) && source !== itemListSources.COST_DATABASE && !isSubAOR && <div className='flex items-center'><IconComponent name="openInViewer" className='hidden group-hover : visible' onClick = {subAorClickHandler}/></div>} */}
      </div>
    );
  }

  const rateTemplate = (
    _row: number,
    _cell: number,
    _value: number | string | null,
    _columnDef: { field?: string },
    params: RateAnalysisRow,
  ) => {
    if (source === ESTIMATE_LEVEL_MEASUREMENT_UNIT) {
      return getEstimateNumberValueBasedOnSettings(params.rate ?? 0, estimateData, "rate");
    } else {
      return typeof params.rate === "number" ? parseFloat(params.rate.toFixed(2)) : "";
    }
  };

  const getDefaultSelectedItemsForCharges = () => {
    const defaultItemToSelect = rateAnalysisDataRef.current.data
      .filter((item: RateAnalysisRow) => {
        if (item.type === rateAnalysisItemType.TOTAL) {
          const selectedSerial = selectedRecordRef.current?.serial;
          if (selectedSerial !== undefined && selectedSerial < (item.serial ?? 0)) {
            return false;
          }
          return true;
        }
      })
      .map((item: RateAnalysisRow) => item.id)
      .filter((id): id is string => Boolean(id));
    return defaultItemToSelect;
  };

  const chargeItemsDatasource = (data: RateAnalysisRow) => {
    let dataElement = [...rateAnalysisDataRef.current.data];
    dataElement = dataElement.filter((item: RateAnalysisRow) => {
      const itemType = item.type;
      if (
        itemType &&
        [rateAnalysisItemType.COST_DATABASE, rateAnalysisItemType.MANUAL, rateAnalysisItemType.MARKETRATE].includes(
          itemType,
        )
      ) {
        return true;
      }
      if (
        item.type === rateAnalysisItemType.ADDITIONAL_CHARGE ||
        item.type === rateAnalysisItemType.SUB_TOTAL ||
        item.type === rateAnalysisItemType.TOTAL
      ) {
        const itemSerial = item.serial ?? 0;
        const dataSerial = data.serial ?? 0;
        if (!data.id || (item.id !== data.id && itemSerial < dataSerial)) {
          const selectedSerial = selectedRecordRef.current?.serial;
          if (selectedSerial !== undefined && selectedSerial < itemSerial) {
            return false;
          }
          return true;
        }
      }
      return false;
    });
    dataElement.map((item: RateAnalysisRow) => {
      let serialTemp;
      const itemType = item.type;
      if (
        itemType &&
        [rateAnalysisItemType.COST_DATABASE, rateAnalysisItemType.MANUAL, rateAnalysisItemType.MARKETRATE].includes(
          itemType,
        )
      ) {
        serialTemp = toRoman(item.serial ?? 0).toLowerCase();
      } else {
        serialTemp = numToChar(item.serial ?? 0);
      }
      item.text = `${serialTemp}-${item.description}`;
    });

    return dataElement;
  };

  const serialTemplate = (
    _row: number,
    _cell: number,
    _value: number | string | null,
    _columnDef: { field?: string },
    params: RateAnalysisRow,
  ) => {
    const { serial, type } = params;

    if (
      type === rateAnalysisItemType.COST_DATABASE ||
      type === rateAnalysisItemType.MANUAL ||
      type === rateAnalysisItemType.MARKETRATE
    ) {
      return serial === undefined ? "" : toRoman(serial).toLowerCase();
    }
    return serial === undefined ? "" : numToChar(serial);
  };

  const serialEditorTemplate = () => {
    const { serial, type } = editingRowValue;
    if (serial === undefined) {
      return "";
    }

    if (
      type === rateAnalysisItemType.COST_DATABASE ||
      type === rateAnalysisItemType.MANUAL ||
      type === rateAnalysisItemType.MARKETRATE
    ) {
      return toRoman(serial).toLowerCase();
    }
    return numToChar(serial);
  };

  const typeTemplate = (
    _row: number,
    _cell: number,
    _value: number | string | null,
    _columnDef: { field?: string },
    params: RateAnalysisRow,
  ) => {
    const { type, costDatabaseName } = params;
    if (type === rateAnalysisItemType.SUB_TOTAL || type === rateAnalysisItemType.TOTAL) {
      return "";
    }
    if (type == rateAnalysisItemType.COST_DATABASE) {
      return costDatabaseName;
    }
    return type;
  };

  const typeEditorTemplate = () => {
    const { type, costDatabaseName } = editingRowValue;
    if (type === rateAnalysisItemType.SUB_TOTAL || type === rateAnalysisItemType.TOTAL) {
      return "";
    }
    if (type == rateAnalysisItemType.COST_DATABASE) {
      return costDatabaseName;
    }
    return type;
  };

  const codeTemplate = (
    _row: number,
    _cell: number,
    _value: number | string | null,
    _columnDef: { field?: string },
    params: RateAnalysisRow,
  ) => {
    const { code, uniqueCode } = params;
    return code || (uniqueCode && uniqueCode.toString().padStart(8, "0")) || "";
  };

  const codeEditorTemplate = () => {
    const { code, uniqueCode } = editingRowValue;
    return code || (uniqueCode && uniqueCode.toString().padStart(8, "0")) || "";
  };

  const descriptionTemplate = (
    _row: number,
    _cell: number,
    _value: number | string | null,
    _columnDef: { field?: string },
    params: RateAnalysisRow,
  ) => {
    const { type, description, selectedItems } = params;
    const container = document.createElement("div");
    const root = createRoot(container);
    if (type === rateAnalysisItemType.ADDITIONAL_CHARGE) {
      let addedOnList = "";
      rateAnalysisDataRef.current.data
        .filter((item: RateAnalysisRow) => selectedItems?.includes(item.id!))
        .map((selectedItem: RateAnalysisRow, index) => {
          let serial: string;
          if (
            selectedItem.type === rateAnalysisItemType.COST_DATABASE ||
            selectedItem.type === rateAnalysisItemType.MANUAL ||
            selectedItem.type === rateAnalysisItemType.MARKETRATE
          ) {
            serial = toRoman(selectedItem.serial!).toLowerCase();
          } else {
            serial = numToChar(selectedItem.serial!);
          }
          addedOnList += index > 0 ? `,${serial}` : serial;
        });
      root.render(
        <div>
          {`${description} on `}
          <span className="font-bold text-sm">{addedOnList}</span>
        </div>,
      );
      return container;
    } else if (type === rateAnalysisItemType.TOTAL || type === rateAnalysisItemType.SUB_TOTAL) {
      root.render(<div className="text-sm font-bold">{description}</div>);
      return container;
    }
    return description;
  };

  function repharseNumberField(
    amount: number,
    decimalField: Parameters<typeof getEstimateNumberValueBasedOnSettings>[2] = "rate",
  ) {
    if (source === ESTIMATE_LEVEL_MEASUREMENT_UNIT) {
      return getEstimateNumberValueBasedOnSettings(amount, estimateData, decimalField);
    } else {
      return amount ? parseFloat(amount.toFixed(2)) : "";
    }
  }

  const amountTemplate = (
    _row: number,
    _cell: number,
    _value: number | string | null,
    _columnDef: { field?: string },
    params: RateAnalysisRow,
  ) => {
    const { amount, type } = params;
    if (typeof amount == "number" && (type === rateAnalysisItemType.TOTAL || type === rateAnalysisItemType.SUB_TOTAL)) {
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(<div className="text-sm font-bold">{repharseNumberField(amount, "amount")}</div>);
      return container;
    }
    return repharseNumberField(Number(amount ?? 0), "amount");
  };

  const attachmentTemplate = (
    _row: number,
    _cell: number,
    _value: number | string | null,
    _columnDef: { field?: string },
    params: RateAnalysisRow,
  ) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <div className="flex h-full items-center">
        <IconComponent
          className="cursor-pointer"
          name="smallAttachmentIcon"
          onClick={attachmentClickHandler(params, false)}
          isEnabled={!isGridInEditMode}
          width="1rem"
          height="1rem"
        />
        {params.attachments && params.attachments.length ? params.attachments.length : ""}
      </div>,
    );
    return container;
  };

  const handleRowEdit = (field: string, value: string | number | null) => {
    isGridInEditMode &&
      setEditingRowValue((prev) => ({
        ...prev,
        [field]: value,
      }));
  };

  function descriptionEditorTemplate(params: { updatedColumn: { field: string }; args: RateAnalysisRow }) {
    const { type, description, selectedItems } = editingRowValue;
    type ChargeItem = RateAnalysisRow & { text?: string };
    let itemDataSourceForCharges: ChargeItem[] = [];
    if (type === rateAnalysisItemType.ADDITIONAL_CHARGE) {
      itemDataSourceForCharges = chargeItemsDatasource(params.args) as ChargeItem[];
    }

    return (
      <div className="flex justify-between gap-1 w-full">
        <TextFieldComponent
          className="flex-1"
          defaultValue={description}
          placeholder=""
          disabled={
            type === rateAnalysisItemType.ADDITIONAL_CHARGE ||
            type === rateAnalysisItemType.COST_DATABASE ||
            type === rateAnalysisItemType.MARKETRATE
          }
          onChange={(e: { target: { value: string } }) => {
            handleRowEdit("description", e.target.value);
          }}
        />
        {type === rateAnalysisItemType.ADDITIONAL_CHARGE && (
          <div className="flex-1">
            <AutoComplete
              limitTags={1}
              multiple
              options={itemDataSourceForCharges}
              valueField="id"
              getOptionLabel={(option: ChargeItem) => option.text || ""}
              defaultValue={itemDataSourceForCharges.filter((record: ChargeItem) =>
                selectedItems?.includes(record.id!),
              )}
              onChange={(_event: React.SyntheticEvent, newValue: ChargeItem[]) => {
                handleRowEdit("selectedItems", newValue.map((v: ChargeItem) => v.id) as unknown as string);
              }}
              renderInput={(p) => <TextFieldComponent {...(p as React.ComponentProps<typeof TextFieldComponent>)} />}
            />
          </div>
        )}
      </div>
    );
  }

  const quantityTemplate = (
    _row: number,
    _cell: number,
    _value: number | string | null,
    _columnDef: { field?: string },
    params: RateAnalysisRow,
  ) => {
    if (source === ESTIMATE_LEVEL_MEASUREMENT_UNIT) {
      return getEstimateNumberValueBasedOnSettings(params.quantity ?? 0, estimateData, "quantity");
    } else {
      return typeof params.quantity === "number" ? parseFloat(params.quantity.toFixed(2)) : "";
    }
  };

  function quantityEditorTemplate(params: { updatedColumn: { field: string } }) {
    const { quantity } = editingRowValue;

    return numericComponent({
      value: quantity ?? 0,
      id: params.updatedColumn.field,
      name: params.updatedColumn.field,
    });
  }

  function unitEditorTemplate(params: { updatedColumn: { field: string } }) {
    const { purchaseUnitsId, type, unit } = editingRowValue;

    return type === rateAnalysisItemType.ADDITIONAL_CHARGE ? (
      <TextFieldComponent value={unit} disabled />
    ) : (
      <AutoComplete
        disabled={type === rateAnalysisItemType.COST_DATABASE || type === rateAnalysisItemType.MARKETRATE}
        options={purchaseUnitsOptions}
        valueField="_id"
        getOptionLabel={(option: NonNullable<GetPurchaseUnitsResponse["result"]>[number]) =>
          option?.nameWithSymbol || ""
        }
        defaultValue={
          purchaseUnitsOptions.find(
            (opt: NonNullable<GetPurchaseUnitsResponse["result"]>[number]) => opt._id === purchaseUnitsId,
          ) || null
        }
        id={params.updatedColumn.field}
        onChange={(
          _event: React.SyntheticEvent,
          newValue: NonNullable<GetPurchaseUnitsResponse["result"]>[number] | null,
        ) => {
          handleRowEdit("purchaseUnitsId", newValue?._id ?? null);
          handleRowEdit("unit", newValue?.name ?? "");
        }}
        renderInput={(inputParams) => (
          <TextFieldComponent {...(inputParams as React.ComponentProps<typeof TextFieldComponent>)} />
        )}
        sx={{ width: "100%", minWidth: "3.125rem" }}
      />
    );
  }

  const showToast = (content: TranslatedString, type: string) => {
    dispatch(showToastNotification({ content, type }));
  };

  const toolbarButtonStateManager = (fieldsToUpdate: { [key: string]: boolean } = {}) => {
    setToolbarButtonsDisabled((prev) => {
      const updatedState = { ...prev } as typeof prev;

      Object.keys(fieldsToUpdate).forEach((key: string) => {
        if (actionPermissionAllowed) {
          updatedState[key] = fieldsToUpdate[key] ?? true;
        } else {
          updatedState[key] = true;
        }
      });

      return updatedState;
    });
  };

  const actionBegin = (args: {
    type?: string;
    requestType?: string;
    action?: string;
    data?: RateAnalysisRow;
    rowData?: RateAnalysisRow[];
    cancel?: boolean;
  }) => {
    if (args.requestType === "add" && args.type == "actionBegin") {
      const links = [...(dataGrid.current?.dataView?.items ?? [])];
      let rowToAdd: RateAnalysisRow;
      if (addedChargeRef?.current) {
        rowToAdd = {
          id: null,
          type: rateAnalysisItemType.ADDITIONAL_CHARGE as RateAnalysisItemType,
          code: "",
          description: addedChargeRef.current.text,
          quantity: addedChargeRef.current.percentage,
          unit: "Percent(%)",
          rate: null,
          amount: null,
          selectedItems: getDefaultSelectedItemsForCharges(),
        };
      } else {
        rowToAdd = {
          id: null,
          type: rateAnalysisItemType.MANUAL as RateAnalysisItemType,
          code: "",
          description: "",
          quantity: null,
          unit: "",
          rate: null,
          amount: null,
        };
      }
      links.unshift(rowToAdd);
      dataGrid.current?.dataView?.setItems?.(links);
      dataGrid.current?.slickGrid.setSelectedRows?.([0]);
      setEditingRowValue(rowToAdd);
      toolbarButtonStateManager({
        [itemDropdownId]: true,
        [chargeDropdownId]: true,
        update: false,
        cancel: false,
      });
    } else if (args.action == "add" && args.requestType == "save") {
      const argsData = args.data as RateAnalysisRow;
      const { description, unit, type, purchaseUnitsId, selectedItems } = argsData;
      if (!type) {
        return;
      }
      if (!description) {
        dispatch(
          showToastNotification({
            content: t("Description is required"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      }
      const rateValue = Number(argsData.rate);
      const quantityValue = Number(argsData.quantity);
      const rate = Number.isFinite(rateValue) ? rateValue : null;
      const quantity = Number.isFinite(quantityValue) ? quantityValue : null;
      let data: RateAnalysisRow;
      if (type === rateAnalysisItemType.ADDITIONAL_CHARGE) {
        if (!selectedItems || !selectedItems.length) {
          return;
        }
        data = {
          description,
          quantity,
          id: uuid(),
          type,
          unit,
          selectedItems,
          chargeId: addedChargeRef.current?.id,
        };
        addedChargeRef.current = null;
      } else {
        data = {
          description,
          rate,
          quantity,
          id: uuid(),
          purchaseUnitsId,
          unit,
          amount: quantity && rate ? quantity * rate : 0,
        };
      }
      prepareRateAnalysisData({ data, action: "add", type });
      setIsGridInEditMode(false);
      setEditingRowValue({});
      dataGrid.current?.refresh?.();

      toolbarButtonStateManager({
        [itemDropdownId]: false,
        [chargeDropdownId]: false,
        edit: true,
        delete: true,
        update: true,
        changeType: true,
        cancel: true,
      });
    } else if (args.requestType === "beginEdit" && args.type == "actionBegin") {
      const selectedRecords = args.rowData as RateAnalysisRow[];
      if (!selectedRecords[0]) {
        return;
      }

      if (isTypeChangedRef.current) {
        setIsGridInEditMode(true);

        toolbarButtonStateManager({
          [itemDropdownId]: false,
          [chargeDropdownId]: false,
          edit: true,
          delete: true,
          update: false,
          cancel: false,
          changeType: true,
        });

        const currentData = _.cloneDeep(selectedRecords[0]);
        currentData.type = rateAnalysisItemType.MANUAL;
        currentData.code = null;
        currentData.uniqueCode = null;
        currentData.costDatabaseId = null;
        currentData.versionId = null;
        setEditingRowValue(currentData);
      } else {
        setIsGridInEditMode(true);

        toolbarButtonStateManager({
          [itemDropdownId]: true,
          [chargeDropdownId]: true,
          edit: true,
          delete: true,
          update: false,
          cancel: false,
          changeType: true,
        });
        setEditingRowValue(_.cloneDeep(selectedRecords[0]));
      }
    } else if (args.action == "edit" && args.requestType === "save") {
      const argsData = args.data as RateAnalysisRow;
      const {
        code,
        uniqueCode,
        costDatabaseId,
        versionId,
        description,
        unit,
        type,
        id,
        serial,
        chargeId,
        costDatabaseName,
        purchaseUnitsId,
        selectedItems,
      } = argsData;
      if (!type) {
        return;
      }
      if (!description) {
        dispatch(
          showToastNotification({
            content: t("Description is required"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      }
      const rateValue = Number(argsData.rate);
      const quantityValue = Number(argsData.quantity);
      const rate = Number.isFinite(rateValue) ? rateValue : null;
      const quantity = Number.isFinite(quantityValue) ? quantityValue : null;
      let data: RateAnalysisRow;

      if (type === rateAnalysisItemType.ADDITIONAL_CHARGE) {
        if (!selectedItems || !selectedItems.length) {
          showToast(t("Please select atleast one item on which you need to add additioanl charge"), TOAST_TYPE_WARNING);
          args.cancel = true;
          return;
        }
        data = {
          description,
          quantity,
          id,
          type,
          serial,
          selectedItems,
          chargeId,
          unit,
        };
        addedChargeRef.current = null;
      } else {
        data = {
          code: isTypeChangedRef.current ? null : code,
          description,
          id,
          type: isTypeChangedRef.current ? rateAnalysisItemType.MANUAL : type,
          serial,
          rate,
          quantity,
          purchaseUnitsId,
          costDatabaseName,
          unit,
          amount: quantity && rate ? quantity * rate : 0,
          uniqueCode: isTypeChangedRef.current ? null : uniqueCode,
          costDatabaseId: isTypeChangedRef.current ? null : costDatabaseId,
          versionId: isTypeChangedRef.current ? null : versionId,
        };
        if (isTypeChangedRef.current) {
          data.isTypeChanged = true;
        }
        isTypeChangedRef.current = null;
      }
      prepareRateAnalysisData({ data, action: "edit" });
      setIsGridInEditMode(false);
      setEditingRowValue({});
      dataGrid.current?.refresh?.();

      toolbarButtonStateManager({
        [itemDropdownId]: false,
        [chargeDropdownId]: false,
        edit: true,
        delete: true,
        update: true,
        cancel: true,
        changeType: true,
      });
    } else if (args.requestType === "cancel") {
      setIsGridInEditMode(false);
      const links = [...(dataGrid.current?.dataView?.items ?? [])].filter((item: RateAnalysisRow) => item.id !== null);
      dataGrid.current?.dataView?.setItems?.(links);
      dataGrid.current?.slickGrid.setSelectedRows?.([]);
      const disableCharge = links.length ? false : true;
      setEditingRowValue({});
      selectedRecordRef.current = null;
      addedChargeRef.current = null;
      isTypeChangedRef.current = null;
      toolbarButtonStateManager({
        [itemDropdownId]: false,
        [chargeDropdownId]: disableCharge,
        edit: true,
        delete: true,
        changeType: true,
        update: true,
        cancel: true,
      });
    }
  };

  const handleRowSelection = () => {
    if (viewMode) {
      return;
    }
    const selectedRecords = dataGrid.current?.slickGrid.getSelectedRows() ?? [];
    const selectedRecordsLength = selectedRecords.length;
    const selectedRecordsData = selectedRecords
      .map((index: number) => dataGrid.current?.slickGrid.getDataItem(index))
      .filter(Boolean) as RateAnalysisRow[];
    const totalIncluded = selectedRecordsData.filter(
      (item: RateAnalysisRow) =>
        item.type === rateAnalysisItemType.TOTAL || item.type === rateAnalysisItemType.SUB_TOTAL,
    );
    if (totalIncluded.length) {
      toolbarButtonStateManager({
        edit: true,
        delete: true,
        changeType: true,
        update: true,
        cancel: true,
      });
      return;
    }
    if (selectedRecordsLength == 0) {
      toolbarButtonStateManager({
        edit: true,
        delete: true,
        changeType: true,
        update: true,
        cancel: true,
      });
    } else if (selectedRecordsLength == 1 && actionPermissionAllowed && selectedRecordsData[0]) {
      const isNewRecord = selectedRecordsData[0].id === null;
      const isCostDataBaseOrMarketRateItem =
        selectedRecordsData[0].type === rateAnalysisItemType.COST_DATABASE ||
        selectedRecordsData[0].type === rateAnalysisItemType.MARKETRATE;
      toolbarButtonStateManager({
        edit: isNewRecord,
        delete: isNewRecord,
        update: !isNewRecord,
        changeType: !isCostDataBaseOrMarketRateItem,
        cancel: !isNewRecord,
      });
    }
  };

  const currencySymbolName = currencyData?.selectedSymbol?.name ?? "";
  const rateAnalysisGridColumns = useMemo(
    () => [
      {
        id: "serial",
        field: "serial",
        name: t("S. No."),
        formatter: serialTemplate,
        _excelValueGetter: (row: RateAnalysisRow) => {
          const { serial, type } = row;
          if (
            type === rateAnalysisItemType.COST_DATABASE ||
            type === rateAnalysisItemType.MANUAL ||
            type === rateAnalysisItemType.MARKETRATE
          ) {
            return serial === undefined ? "" : toRoman(serial).toLowerCase();
          }
          return serial === undefined ? "" : numToChar(serial);
        },
        editorTemplate: serialEditorTemplate,
        minWidth: 20,
      },
      {
        id: "type",
        field: "type",
        name: t("Type"),
        formatter: typeTemplate,
        _excelValueGetter: (row: RateAnalysisRow) => {
          const { type, costDatabaseName } = row;
          if (type === rateAnalysisItemType.SUB_TOTAL || type === rateAnalysisItemType.TOTAL) {
            return "";
          }
          if (type == rateAnalysisItemType.COST_DATABASE) {
            return costDatabaseName ?? "";
          }
          return type ?? "";
        },
        editorTemplate: typeEditorTemplate,
        minWidth: 30,
      },
      {
        id: "code",
        field: "code",
        name: t("Code"),
        formatter: codeTemplate,
        _excelValueGetter: (row: RateAnalysisRow) => {
          const { code, uniqueCode } = row;
          return code || (uniqueCode && uniqueCode.toString().padStart(8, "0")) || "";
        },
        editorTemplate: codeEditorTemplate,
        minWidth: 35,
      },
      {
        id: "description",
        field: "description",
        name: t("Description"),
        formatter: descriptionTemplate,
        _excelValueGetter: (row: RateAnalysisRow) => row.description ?? "",
        editorTemplate: descriptionEditorTemplate,
        minWidth: 200,
      },
      {
        id: "attachments",
        field: "attachments",
        name: "",
        formatter: attachmentTemplate,
        excludeFromExport: true,
        disableGrouping: true,
        disableFiltering: true,
        disableSorting: true,
        disableMenuOptions: true,
        minWidth: 40,
        maxWidth: 40,
      },
      {
        id: "quantity",
        field: "quantity",
        name: t("Quantity"),
        formatter: quantityTemplate,
        _excelValueGetter: (row: RateAnalysisRow) => {
          if (source === ESTIMATE_LEVEL_MEASUREMENT_UNIT) {
            return getEstimateNumberValueBasedOnSettings(row.quantity ?? 0, estimateData, "quantity");
          }
          return typeof row.quantity === "number" ? parseFloat(row.quantity.toFixed(2)) : "";
        },
        editorTemplate: quantityEditorTemplate,
        minWidth: 30,
      },
      {
        id: "unit",
        field: "unit",
        name: t("Unit"),
        formatter: (a, b, c, d, params) => params.unit,
        editorTemplate: unitEditorTemplate,
        minWidth: 50,
      },
      {
        id: "rate",
        field: "rate",
        name: t("Rate({{currency}})", { currency: currencySymbolName }),
        formatter: rateTemplate,
        _excelValueGetter: (row: RateAnalysisRow) => {
          if (source === ESTIMATE_LEVEL_MEASUREMENT_UNIT) {
            return getEstimateNumberValueBasedOnSettings(row.rate ?? 0, estimateData, "rate");
          }
          return typeof row.rate === "number" ? parseFloat(row.rate.toFixed(2)) : "";
        },
        editorTemplate: rateEditorTemplate,
        minWidth: 30,
      },
      {
        id: "amount",
        field: "amount",
        name: t("Amount({{currency}})", { currency: currencySymbolName }),
        formatter: amountTemplate,
        _excelValueGetter: (row: RateAnalysisRow) => repharseNumberField(Number(row.amount ?? 0), "amount"),
        minWidth: 30,
      },
    ],

    [isGridInEditMode, currencySymbolName],
  );

  const rateAnalysisGrid = () => {
    return (
      <SlickGrid1
        ref={dataGrid as React.Ref<React.ComponentRef<typeof SlickGrid1>>}
        columnDefinitions={
          rateAnalysisGridColumns as unknown as React.ComponentProps<typeof SlickGrid1>["columnDefinitions"]
        }
        gridOptions={{
          enableAutoSizeColumns: true,
        }}
        rowHeight={35}
        dataset={rateAnalysisData.data as React.ComponentProps<typeof SlickGrid1>["dataset"]}
        isEditMode={isGridInEditMode}
        onSelectedRowIdsChanged={handleRowSelection}
        toolbar={toolbarOptions}
        toolbarClickHandler={toolbarClickHandler}
        showPageCount={false}
        selectCheckBoxOnRowClick
        enableFilteringOption
        enableSortingOption
        enableGroupingOption
        enableColumnChooser
        hideNoDataIllustration
      />
    );
  };

  const handleNumericComponentChange =
    (name: string) => (e: { value: number | null; target: { value: string; name?: string } }) => {
      const nextValue = e.value ?? 0;
      let cellValue = nextValue;
      if (e.target.name == "say") {
        setRateAnalysisData({ ...rateAnalysisDataRef.current, say: nextValue });
        setIsUserPerformedAction(true);
      } else if (e.target.name === "purchaseUnit") {
        setPurchaseUnitCount(nextValue);
        const updated = { ...rateAnalysisDataRef.current };
        cellValue = calculateSay(updated, nextValue);
        setRateAnalysisData({ ...updated, say: cellValue });
        setIsUserPerformedAction(true);
      }
      handleRowEdit(name, cellValue);
    };

  const numericComponent = ({
    id,
    name,
    value,
    step,
    min,
    disabled = false,
  }: {
    id?: string;
    name: string;
    value: number | null | undefined;
    step?: number;
    min?: number;
    disabled?: boolean;
  }) => (
    <NumericTextBoxComponent
      id={id}
      name={name}
      showSpinButton={false}
      disabled={disabled || viewMode || !actionPermissionAllowed}
      min={min}
      value={value ?? 0}
      onChange={handleNumericComponentChange(name)}
      step={step || 1}
    />
  );

  const handleCloseAddCostDatabaseModal = () => {
    closeConfirmModal();
    setOpenCostDatabaseModal(false);
    setOpenMarketRateDatabaseModal(false);
    setImportFromCostDatabase(false);
    setImportFrommarketRateDatabase(false);
    setSelectedCostDatabaseId(null);
    isItemsPage.current = false;
    setCheckedCostDatabaseIds([]);
    setCheckedCostDatabaseItemIds([]);
    setCheckedMarketrateItemIds([]);
  };

  const customSort = (rateAnalysisList: RateAnalysisRow[]) => {
    const items = rateAnalysisList
      .filter(
        (item: RateAnalysisRow) =>
          item.type === rateAnalysisItemType.COST_DATABASE ||
          item.type === rateAnalysisItemType.MANUAL ||
          item.type === rateAnalysisItemType.MARKETRATE,
      )
      .sort((a: RateAnalysisRow, b: RateAnalysisRow) => (a.serial ?? 0) - (b.serial ?? 0));
    const additionalItems = rateAnalysisList
      .filter(
        (item: RateAnalysisRow) =>
          !(
            item.type === rateAnalysisItemType.COST_DATABASE ||
            item.type === rateAnalysisItemType.MANUAL ||
            item.type === rateAnalysisItemType.MARKETRATE
          ),
      )
      .sort((a: RateAnalysisRow, b: RateAnalysisRow) => (a.serial ?? 0) - (b.serial ?? 0));
    return [...items, ...additionalItems];
  };

  const amountChangedDueToSubTotal = (
    itemId: string,
    dataArray: RateAnalysisRow[],
    amountChanged: { [id: string]: number },
    chargeDeletedDetails?: RateAnalysisRow[],
  ) => {
    const selectedItem = dataArray.find((item: RateAnalysisRow) => item.id === itemId);
    if (!selectedItem) return 0;
    const filterDataArray = chargeDeletedDetails || dataArray;
    const allElement = filterDataArray.filter((item: RateAnalysisRow) => {
      const itemType = item.type;
      return (
        (itemType &&
          [rateAnalysisItemType.COST_DATABASE, rateAnalysisItemType.MANUAL, rateAnalysisItemType.MARKETRATE].includes(
            itemType,
          )) ||
        (item.serial ?? 0) < (selectedItem.serial ?? 0)
      );
    });
    let changeInAmount = 0;
    allElement.map((item: RateAnalysisRow) => {
      if (item.id && Object.hasOwn(amountChanged, item.id)) {
        changeInAmount += amountChanged[item.id] ?? 0;
      }
    });
    return changeInAmount;
  };

  const prepareRateAnalysisData = (args: PrepareRateAnalysisArgs) => {
    const updatedAnalysisData: RateAnalysisData = { ...rateAnalysisDataRef.current };
    const itemsConst = [
      rateAnalysisItemType.COST_DATABASE,
      rateAnalysisItemType.MANUAL,
      rateAnalysisItemType.MARKETRATE,
    ];
    const additionalConst = [
      rateAnalysisItemType.ADDITIONAL_CHARGE,
      rateAnalysisItemType.SUB_TOTAL,
      rateAnalysisItemType.TOTAL,
    ];

    if (args.action == "add") {
      const { data, type } = args;
      let currentTotal = updatedAnalysisData.total || 0;
      let additionalSerialCount = updatedAnalysisData.data.filter(
        (item: RateAnalysisRow) => !itemsConst.includes(item.type as RateAnalysisItemType),
      ).length;

      const isTotalExist = updatedAnalysisData.data.find(
        (item: RateAnalysisRow) => item.type === rateAnalysisItemType.TOTAL,
      );
      const needToAddTotalRow = !isTotalExist || (!itemsConst.includes(type) && !selectedRecordRef.current);
      if (needToAddTotalRow) {
        updatedAnalysisData.data.map((item: RateAnalysisRow) => {
          if (item.type === rateAnalysisItemType.TOTAL) {
            item.description = "Sub Total";
            item.type = rateAnalysisItemType.SUB_TOTAL;
          }
        });
      }

      if (itemsConst.includes(type)) {
        let serialCount: number;
        if (selectedRecordRef.current) {
          const selectedSerial = selectedRecordRef.current.serial ?? 0;
          serialCount = selectedSerial + 1;
          updatedAnalysisData.data.map((item: RateAnalysisRow) => {
            if (itemsConst.includes(item.type as RateAnalysisItemType) && item.serial && item.serial >= serialCount) {
              item.serial = (item.serial ?? 0) + 1;
            }
          });
        } else {
          serialCount =
            updatedAnalysisData.data.filter((item: RateAnalysisRow) =>
              itemsConst.includes(item.type as RateAnalysisItemType),
            ).length + 1;
        }
        updatedAnalysisData.data.push({
          ...data,
          serial: serialCount,
          type,
        });
      } else if (type === rateAnalysisItemType.ADDITIONAL_CHARGE) {
        const selectedItems = data.selectedItems ?? [];
        const selectedItemsData = updatedAnalysisData.data.filter(
          (item: RateAnalysisRow) => item.id !== null && selectedItems.includes(item.id),
        );
        const baseAmount = selectedItemsData.reduce(
          (total: number, item: RateAnalysisRow) => total + (item.amount ?? 0),
          0,
        );
        const chargeQuantity = Number(data.quantity ?? 0);
        let amount = (baseAmount * chargeQuantity) / 100;
        data.amount = amount;
        // data.selectedItems = selectedItemsData.map(item=>({
        //   id : item.id,
        //   description : item.description,
        //   serial : item.serial
        // }))
        if (selectedRecordRef.current) {
          additionalSerialCount = selectedRecordRef.current.serial ?? 0;
          updatedAnalysisData.data.map((item: RateAnalysisRow) => {
            const itemType = item.type;
            if (itemType && additionalConst.includes(itemType) && (item.serial ?? 0) > additionalSerialCount) {
              if (itemType === rateAnalysisItemType.SUB_TOTAL) {
                item.amount = (item.amount ?? 0) + (data.amount ?? 0);
              }
              item.serial = (item.serial ?? 0) + 2;
            }
          });
          updatedAnalysisData.data.push({
            description: "Sub Total",
            amount: (selectedRecordRef.current.amount ?? 0) + (data.amount ?? 0),
            type: rateAnalysisItemType.SUB_TOTAL,
            id: uuid(),
            serial: additionalSerialCount + 2,
          });
        }

        additionalSerialCount += 1;
        updatedAnalysisData.data.push({
          ...data,
          type,
          serial: additionalSerialCount,
        });
      }
      currentTotal += Number(data.amount ?? 0);
      updatedAnalysisData.total = currentTotal;
      updatedAnalysisData.say = currentTotal;

      if (itemsConst.includes(type)) {
        updatedAnalysisData.data.map((item: RateAnalysisRow) => {
          if (item.type === rateAnalysisItemType.SUB_TOTAL) {
            item.amount = (item.amount ?? 0) + (data.amount ?? 0);
          }
        });
      }

      if (needToAddTotalRow) {
        updatedAnalysisData.data.push({
          description: "Total",
          amount: currentTotal,
          type: rateAnalysisItemType.TOTAL,
          id: uuid(),
          serial: additionalSerialCount + 1,
        });
      } else {
        updatedAnalysisData.data.map((item: RateAnalysisRow) => {
          if (item.type === rateAnalysisItemType.TOTAL) {
            item.amount = currentTotal;
          }
        });
      }

      if (itemsConst.includes(type)) {
        updatedAnalysisData.data = customSort(updatedAnalysisData.data);
        let amountChanged: { [id: string]: number } = {};
        let updateAmount = Number(data.amount ?? 0);
        updatedAnalysisData.data.map((item: RateAnalysisRow) => {
          if (item.type === rateAnalysisItemType.ADDITIONAL_CHARGE && item.selectedItems?.length) {
            const itemId = item.id;
            if (!itemId) {
              return;
            }
            item.selectedItems.map((selectedItem: string) => {
              const selectedItemType = updatedAnalysisData.data.find(
                (itemData: RateAnalysisRow) => itemData.id === selectedItem,
              )?.type;
              if (
                selectedItemType &&
                [rateAnalysisItemType.SUB_TOTAL, rateAnalysisItemType.TOTAL].includes(
                  selectedItemType as RateAnalysisItemType,
                )
              ) {
                // const updateAmount =  amountChangedDueToSubTotal(selectedItem, updatedAnalysisData.data, amountChanged)

                if (Object.hasOwn(amountChanged, itemId)) {
                  amountChanged[itemId] =
                    (amountChanged[itemId] ?? 0) + (updateAmount * Number(item.quantity ?? 0)) / 100;
                } else {
                  amountChanged[itemId] = (updateAmount * Number(item.quantity ?? 0)) / 100;
                }
              }
            });

            if (Object.hasOwn(amountChanged, itemId)) {
              const amountDelta = amountChanged[itemId] ?? 0;
              item.amount = (item.amount ?? 0) + amountDelta;
              updateAmount += amountDelta;
              amountChanged = {};
            }
          }
          if (item.type === rateAnalysisItemType.SUB_TOTAL || item.type === rateAnalysisItemType.TOTAL) {
            item.amount = (item.amount ?? 0) + updateAmount - Number(data.amount ?? 0);
          }
        });

        updatedAnalysisData.total += updateAmount - Number(data.amount ?? 0);
        updatedAnalysisData.say += updateAmount - Number(data.amount ?? 0);
      }
      selectedRecordRef.current = null;
    } else if (args.action == "edit") {
      const { data } = args;
      if (!data.id) {
        return;
      }
      const dataId = data.id;
      const amountChanged: { [id: string]: number } = {};
      const { isTypeChanged, prevItemId } = data;
      delete data.prevItemId;
      delete data.isTypeChanged;

      if (prevItemId) {
        updatedAnalysisData.data = updatedAnalysisData.data.map((item: RateAnalysisRow) => {
          if (item.id === prevItemId) {
            return { ...item, type: data.type, id: dataId };
          }
          if (item.selectedItems?.length) {
            item.selectedItems = item.selectedItems.map((itemId: string) => {
              if (itemId === prevItemId) {
                return dataId;
              }
              return itemId;
            });
          }
          return item;
        });
      }
      if (data.type === rateAnalysisItemType.ADDITIONAL_CHARGE) {
        const selectedItems = data.selectedItems ?? [];
        const selectedItemsData = updatedAnalysisData.data.filter(
          (item: RateAnalysisRow) => item.id !== null && selectedItems.includes(item.id),
        );
        const baseAmount = selectedItemsData.reduce(
          (total: number, item: RateAnalysisRow) => total + (item.amount ?? 0),
          0,
        );
        const chargeQuantity = Number(data.quantity ?? 0);
        let amount = (baseAmount * chargeQuantity) / 100;
        data.amount = amount;
      }
      updatedAnalysisData.data = updatedAnalysisData.data.map((item: RateAnalysisRow) => {
        if (item.id === dataId) {
          amountChanged[dataId] = Number(data.amount ?? 0) - Number(item.amount ?? 0);
          if (data.type === rateAnalysisItemType.COST_DATABASE || data.type === rateAnalysisItemType.MARKETRATE) {
            let latestItem = { ...data };
            if (prevItemId) {
              latestItem = {
                ...latestItem,
                serial: item.serial,
              };
            }
            return latestItem;
          }
          if (data.type === rateAnalysisItemType.MANUAL) {
            if (isTypeChanged) {
              const newItemId = uuid();
              updatedAnalysisData.data.map((item: RateAnalysisRow) => {
                if (item.type === rateAnalysisItemType.ADDITIONAL_CHARGE) {
                  item.selectedItems = item.selectedItems?.map((selectedItem: string) => {
                    if (selectedItem === dataId) {
                      return newItemId;
                    }
                    return selectedItem;
                  });
                }
              });
              amountChanged[newItemId] = amountChanged[dataId];
              delete amountChanged[dataId];
              data.id = newItemId;
            }
            return data;
          }
          if (item.type === rateAnalysisItemType.ADDITIONAL_CHARGE) {
            return data;
          }
        }
        return item;
      });

      updatedAnalysisData.data = customSort(updatedAnalysisData.data);
      let totalAmount = 0;
      updatedAnalysisData.data.map((item: RateAnalysisRow) => {
        if (item.type === rateAnalysisItemType.ADDITIONAL_CHARGE && item.id !== data.id) {
          const itemId = item.id;
          if (!itemId) {
            return;
          }
          item.selectedItems?.map((selectedItem: string) => {
            const selectedItemType = updatedAnalysisData.data.find(
              (itemData: RateAnalysisRow) => itemData.id === selectedItem,
            )?.type;
            if (
              selectedItemType &&
              [rateAnalysisItemType.SUB_TOTAL, rateAnalysisItemType.TOTAL].includes(
                selectedItemType as RateAnalysisItemType,
              )
            ) {
              const updateAmount = amountChangedDueToSubTotal(selectedItem, updatedAnalysisData.data, amountChanged);

              if (Object.hasOwn(amountChanged, itemId)) {
                amountChanged[itemId] =
                  (amountChanged[itemId] ?? 0) + (updateAmount * Number(item.quantity ?? 0)) / 100;
              } else {
                amountChanged[itemId] = (updateAmount * Number(item.quantity ?? 0)) / 100;
              }
            } else if (Object.hasOwn(amountChanged, selectedItem)) {
              if (Object.hasOwn(amountChanged, itemId)) {
                amountChanged[itemId] =
                  (amountChanged[itemId] ?? 0) +
                  ((amountChanged[selectedItem] ?? 0) * Number(item.quantity ?? 0)) / 100;
              } else {
                amountChanged[itemId] = ((amountChanged[selectedItem] ?? 0) * Number(item.quantity ?? 0)) / 100;
              }
            }
          });
          if (Object.hasOwn(amountChanged, itemId)) {
            item.amount = (item.amount ?? 0) + (amountChanged[itemId] ?? 0);
          }
        } else if (
          (item.type === rateAnalysisItemType.TOTAL || item.type === rateAnalysisItemType.SUB_TOTAL) &&
          (data.type !== rateAnalysisItemType.ADDITIONAL_CHARGE || (item.serial ?? 0) > (data.serial ?? 0))
        ) {
          totalAmount = Object.values(amountChanged).reduce((sum: number, value: number) => sum + value, 0);
          item.amount = (item.amount ?? 0) + totalAmount;
        }
      });

      updatedAnalysisData.total += totalAmount;
      updatedAnalysisData.say += totalAmount;
    } else if (args.action === "delete") {
      const { data } = args;
      const deletedObjectAmount: { [id: string]: number } = {};
      const chargeDeletedDetails: RateAnalysisRow[] = [];
      updatedAnalysisData.data = updatedAnalysisData.data.filter((item: RateAnalysisRow) => {
        if (item.id !== null && data.includes(item.id)) {
          const itemId = item.id;
          chargeDeletedDetails.push({ ...item });
          deletedObjectAmount[itemId] = -Number(item.amount ?? 0);

          if (item.type === rateAnalysisItemType.ADDITIONAL_CHARGE) {
            const belowSubtotal = updatedAnalysisData.data.find(
              (dataItem: RateAnalysisRow) =>
                (dataItem.type == rateAnalysisItemType.SUB_TOTAL || dataItem.type === rateAnalysisItemType.TOTAL) &&
                dataItem.serial === (item.serial ?? 0) + 1,
            );
            if (belowSubtotal?.type === rateAnalysisItemType.SUB_TOTAL && belowSubtotal.id) {
              chargeDeletedDetails.push({ ...belowSubtotal });
              deletedObjectAmount[belowSubtotal.id] = -Number(belowSubtotal.amount ?? 0);
            } else if (belowSubtotal?.type === rateAnalysisItemType.TOTAL) {
              const aboveSubTotal = updatedAnalysisData.data.find(
                (dataItem: RateAnalysisRow) =>
                  dataItem.type == rateAnalysisItemType.SUB_TOTAL && dataItem.serial === (item.serial ?? 0) - 1,
              );
              if (aboveSubTotal?.id) {
                chargeDeletedDetails.push({ ...aboveSubTotal });
                deletedObjectAmount[aboveSubTotal.id] = -Number(aboveSubTotal.amount ?? 0);
              }
            }
          }
          return false;
        }
        return true;
      });

      updatedAnalysisData.data = updatedAnalysisData.data.filter(
        (item: RateAnalysisRow) => !(item.id && Object.keys(deletedObjectAmount).includes(item.id)),
      );

      // above subtotal
      if (
        !updatedAnalysisData.data.find((item: RateAnalysisRow) => item.type === rateAnalysisItemType.ADDITIONAL_CHARGE)
      ) {
        updatedAnalysisData.data = updatedAnalysisData.data.filter((item: RateAnalysisRow) => {
          if (item.type === rateAnalysisItemType.SUB_TOTAL) {
            chargeDeletedDetails.push({ ...item });
            if (item.id) {
              deletedObjectAmount[item.id] = -Number(item.amount ?? 0);
            }
            return false;
          }
          return true;
        });
      }

      updatedAnalysisData.data = customSort(updatedAnalysisData.data);
      let totalChangedAmount = 0;
      updatedAnalysisData.data.map((item: RateAnalysisRow) => {
        if (item.type === rateAnalysisItemType.ADDITIONAL_CHARGE && !(item.id && data.includes(item.id))) {
          const itemId = item.id;
          if (!itemId) {
            return;
          }
          item.selectedItems?.map((selectedItem: string) => {
            const selectedItemType = updatedAnalysisData.data.find(
              (itemData: RateAnalysisRow) => itemData.id === selectedItem,
            )?.type;
            if (
              selectedItemType &&
              [rateAnalysisItemType.SUB_TOTAL, rateAnalysisItemType.TOTAL].includes(
                selectedItemType as RateAnalysisItemType,
              )
            ) {
              const updateAmount = amountChangedDueToSubTotal(
                selectedItem,
                updatedAnalysisData.data,
                deletedObjectAmount,
                chargeDeletedDetails,
              );

              if (Object.hasOwn(deletedObjectAmount, itemId)) {
                deletedObjectAmount[itemId] =
                  (deletedObjectAmount[itemId] ?? 0) + (updateAmount * Number(item.quantity ?? 0)) / 100;
              } else {
                deletedObjectAmount[itemId] = (updateAmount * Number(item.quantity ?? 0)) / 100;
              }
            } else if (Object.hasOwn(deletedObjectAmount, selectedItem)) {
              if (Object.hasOwn(deletedObjectAmount, itemId)) {
                deletedObjectAmount[itemId] =
                  (deletedObjectAmount[itemId] ?? 0) +
                  ((deletedObjectAmount[selectedItem] ?? 0) * Number(item.quantity ?? 0)) / 100;
              } else {
                deletedObjectAmount[itemId] =
                  ((deletedObjectAmount[selectedItem] ?? 0) * Number(item.quantity ?? 0)) / 100;
              }
            }
          });
          if (Object.hasOwn(deletedObjectAmount, itemId)) {
            item.amount = (item.amount ?? 0) + (deletedObjectAmount[itemId] ?? 0);
          }
          item.selectedItems = (item.selectedItems ?? []).filter((itemId: string) => !data.includes(itemId));
        } else if (item.type === rateAnalysisItemType.TOTAL || item.type === rateAnalysisItemType.SUB_TOTAL) {
          const effectivedeletedAmountForThisLevel: { [id: string]: number } = {};
          Object.keys(deletedObjectAmount).map((deletedItemId: string) => {
            const itemDetail = chargeDeletedDetails.find((item: RateAnalysisRow) => item.id == deletedItemId);
            if (
              !itemDetail ||
              (itemDetail.type !== rateAnalysisItemType.SUB_TOTAL &&
                !(
                  itemDetail.type === rateAnalysisItemType.ADDITIONAL_CHARGE &&
                  (itemDetail.serial ?? 0) > (item.serial ?? 0)
                ))
            ) {
              effectivedeletedAmountForThisLevel[deletedItemId] = deletedObjectAmount[deletedItemId] ?? 0;
            }
          });
          totalChangedAmount = Object.values(effectivedeletedAmountForThisLevel).reduce(
            (sum: number, value: number) => sum + value,
            0,
          );
          item.amount = (item.amount ?? 0) + totalChangedAmount;
        }
      });

      updatedAnalysisData.total += totalChangedAmount;
      updatedAnalysisData.say += totalChangedAmount;

      const isItemRecordExist = updatedAnalysisData.data.filter((item: RateAnalysisRow) =>
        itemsConst.includes(item.type as RateAnalysisItemType),
      );
      if (isItemRecordExist.length === 0) {
        updatedAnalysisData.data = [];
        updatedAnalysisData.total = 0;
        updatedAnalysisData.say = 0;
      } else {
        // serial logic
        chargeDeletedDetails.map((deletedItem: RateAnalysisRow) => {
          const change = 1;

          // if(deletedItem.type === rateAnalysisItemType.ADDITIONAL_CHARGE){
          //   updatedAnalysisData.data = updatedAnalysisData.data.filter(item=> !(item.type == rateAnalysisItemType.SUB_TOTAL && item.serial === deletedItem.serial + 1 ))
          //   if(!updatedAnalysisData.data.find(item=> item.type === rateAnalysisItemType.ADDITIONAL_CHARGE) && updatedAnalysisData.data.find(item=>item.type === rateAnalysisItemType.SUB_TOTAL)){
          //     updatedAnalysisData.data = updatedAnalysisData.data.filter(item=> item.type !== rateAnalysisItemType.SUB_TOTAL)
          //   }
          //   change +=1
          // }

          updatedAnalysisData.data.map((item: RateAnalysisRow) => {
            const itemSerial = item.serial ?? 0;
            const deletedSerial = deletedItem.serial ?? 0;
            if (
              itemsConst.includes(item.type as RateAnalysisItemType) &&
              itemsConst.includes(deletedItem.type as RateAnalysisItemType) &&
              itemSerial > deletedSerial
            ) {
              item.serial = itemSerial - change;
            } else if (
              deletedItem.type === rateAnalysisItemType.SUB_TOTAL &&
              additionalConst.includes(item.type as RateAnalysisItemType) &&
              itemSerial >= deletedSerial
            ) {
              item.serial = itemSerial - change;
            } else if (
              additionalConst.includes(item.type as RateAnalysisItemType) &&
              additionalConst.includes(deletedItem.type as RateAnalysisItemType) &&
              itemSerial > deletedSerial
            ) {
              item.serial = itemSerial - change;
            }
            // if(((itemsConst.includes(item.type) && itemsConst.includes(deletedItem.type)) || (additionalConst.includes(item.type) && additionalConst.includes(deletedItem.type)))  && item.serial > deletedItem.serial){
            //   item.serial = item.serial - change
            // }
          });
        });
      }
    }
    updatedAnalysisData.data = customSort(updatedAnalysisData.data);
    updatedAnalysisData.say = calculateSay(updatedAnalysisData);
    if (updatedAnalysisData.data.length > 0) {
      toolbarButtonStateManager({
        [chargeDropdownId]: false,
      });
    } else {
      toolbarButtonStateManager({
        [chargeDropdownId]: true,
      });
    }
    setRateAnalysisData(updatedAnalysisData);
    setIsUserPerformedAction(true);
  };

  const handleNextToItemsPage = async () => {
    if (!selectedCostDatabaseId && !importFrommarketRateDatabase && !openMarketRateDatabaseModal) {
      isItemsPage.current = true;
      setSelectedCostDatabaseId(checkedCostDatabaseIds[0] ?? null);
    } else if (!importFromCostDatabase && !importFrommarketRateDatabase) {
      const recordSelected = openMarketRateDatabaseModal ? checkedMarketrateItemIds[0] : checkedCostDatabaseItemIds[0];
      if (!recordSelected) {
        return;
      }
      const recordExist = rateAnalysisData.data.find((item: RateAnalysisRow) => item.id === recordSelected._id);
      if (recordExist) {
        showToast(t("Item already added!"), TOAST_TYPE_WARNING);
        return;
      }
      const data: RateAnalysisRow = {
        id: recordSelected._id,
        description: recordSelected.description,
        rate: recordSelected.rate,
        unit: recordSelected.purchaseUnit?.name,
        quantity: 0,
        amount: 0,
        purchaseUnitsId: recordSelected.purchaseUnit?.id,
        costDatabaseId: recordSelected.costDatabaseId,
        versionId: recordSelected.versionId,
      };
      if (openMarketRateDatabaseModal) {
        data.uniqueCode = recordSelected.uniqueCode;
      } else {
        data.code = recordSelected.code;
        data.costDatabaseName = checkedCostDatabaseIds[0]?.name;
      }
      prepareRateAnalysisData({
        data,
        action: "add",
        type: openMarketRateDatabaseModal ? rateAnalysisItemType.MARKETRATE : rateAnalysisItemType.COST_DATABASE,
      });
      handleCloseAddCostDatabaseModal();
    } else {
      setShowConfirmImportModal(true);
    }
  };

  const handleImportRateAnalysis = () => {
    const recordSelected = checkedMarketrateItemIds[0] ?? checkedCostDatabaseItemIds[0];
    if (!recordSelected) {
      return;
    }
    let { rateAnalysis } = recordSelected;
    if (!rateAnalysis) {
      rateAnalysis = {
        purchaseUnitCount: 1,
        rateAnalysisData: { data: [], total: 0, say: 0 },
      };
    }

    setPurchaseUnitCount(rateAnalysis.purchaseUnitCount);
    setRateAnalysisData(rateAnalysis.rateAnalysisData);
    setIsUserPerformedAction(true);
    handleCloseAddCostDatabaseModal();
  };

  const closeConfirmModal = () => {
    setShowConfirmImportModal(false);
  };

  const handleRecordsDelete = (records: RateAnalysisRow[]) => {
    prepareRateAnalysisData({
      data: records.map((record: RateAnalysisRow) => record.id).filter((id): id is string => Boolean(id)),
      action: "delete",
    });
    toolbarButtonStateManager({
      [itemDropdownId]: false,
      [chargeDropdownId]: rateAnalysisDataRef.current.data.length - records.length < 2,
      edit: true,
      delete: true,
      changeType: true,
    });
  };

  const costDatabaseModal = () => (
    <ModalStateDialogComponent
      isVisible={
        openCostDatabaseModal || openMarketRateDatabaseModal || importFromCostDatabase || importFrommarketRateDatabase
      }
      allowModalPadding={false}
      footerContent={
        <div className="flex h-4 items-center gap-6 ">
          <ButtonComponent
            onClick={handleNextToItemsPage}
            disabled={
              selectedCostDatabaseId
                ? checkedCostDatabaseItemIds.length !== 1
                : checkedMarketrateItemIds.length
                  ? checkedMarketrateItemIds.length != 1
                  : checkedCostDatabaseIds.length !== 1
            }
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          >
            <Text>{selectedCostDatabaseId ? t("Add") : t("Next")}</Text>
          </ButtonComponent>
          <ButtonComponent onClick={handleCloseAddCostDatabaseModal} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      }
      onCancel={handleCloseAddCostDatabaseModal}
      headerTitle={
        <div className=" w-full flex justify-between overflow-auto items-center">
          <span>
            {(openCostDatabaseModal ||
              openMarketRateDatabaseModal ||
              importFromCostDatabase ||
              importFrommarketRateDatabase) &&
            !selectedCostDatabaseId &&
            !importFrommarketRateDatabase &&
            !openMarketRateDatabaseModal
              ? "Cost Databases"
              : importFrommarketRateDatabase || openMarketRateDatabaseModal
                ? "Choose Item(s)"
                : `Choose Item(s) | ${selectedCostDatabaseId?.name ?? ""}`}
          </span>
        </div>
      }
      width="90%"
      height="95%"
      modelContent={
        <div className="flex h-full overflow-auto">
          {!selectedCostDatabaseId &&
            !importFrommarketRateDatabase &&
            !openMarketRateDatabaseModal &&
            !params.costDatabaseVersionId && (
              <CostDatabases
                showOnlyGrid
                costDatabaseSelection={costDatabaseSelection}
                costDatabaseQuery={{
                  publishedVersion: true,
                  params: {
                    publishedVersion: true,
                  },
                }}
              />
            )}
          {selectedCostDatabaseId && (
            <CostDatabase
              costDatabaseId={selectedCostDatabaseId._id}
              showOnlyGrid
              costDatabaseItemsSelection={
                costDatabaseItemsSelection as React.ComponentProps<typeof CostDatabase>["costDatabaseItemsSelection"]
              }
              publishedVersion={true}
            />
          )}
          {/* {(importFrommarketRateDatabase || openMarketRateDatabaseModal) &&  <MarketRateItems  showOnlyGrid= {true} marketRateDatabaseItemsSelection = {marketRateDatabaseItemsSelection} source={itemListSources.IMPORT_FROM_MARKETRATE}/>} */}
        </div>
      }
    />
  );

  const confirmImportModal = () => (
    <ModalStateDialogComponent
      isVisible={showConfirmImportModal}
      allowModalPadding={false}
      onCancel={closeConfirmModal}
      footerContent={
        <div className="flex items-center gap-6 ">
          <ButtonComponent onClick={handleImportRateAnalysis} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
            <Text>{t("Confirm")}</Text>
          </ButtonComponent>
          <ButtonComponent onClick={closeConfirmModal} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      }
      headerTitle={
        <div className="  w-full flex justify-between overflow-auto items-center">
          <Text>{t("Override Rate Analysis")}</Text>
        </div>
      }
      width="27.5rem"
      modelContent={
        <Text sx={{ whiteSpace: "normal" }} className="">
          {t("This will override the existing Rate Analysis with the chosen item’s Rate analysis.")}
        </Text>
      }
    />
  );

  const costDatabaseSelection = (selectedRecords: CostDatabaseRecord[]) => {
    if (!isItemsPage.current) {
      setCheckedCostDatabaseIds(selectedRecords);
    }
  };

  const costDatabaseItemsSelection = (selectedRecords: CostDatabaseItemRecord[]) => {
    setCheckedCostDatabaseItemIds(selectedRecords);
  };

  // const marketRateDatabaseItemsSelection = (params) => {
  //   if (params.name == "rowDeselected") {
  //     setCheckedMarketrateItemIds((prev) => prev.filter((item: any) => item.id !== params.data.id));
  //   } else {
  //     setCheckedMarketrateItemIds((prev) => [...prev, params.data]);
  //   }
  // };

  const refreshSayAmount = () => {
    const updated = { ...rateAnalysisDataRef.current };
    setRateAnalysisData({ ...updated, say: calculateSay(updated) });
    setIsUserPerformedAction(true);
  };

  const importButton = () => (
    // <SelectDropDown
    //   buttonType="dropdownBackgroundButton"
    //   allowButtonPadding={false}
    //   select={handleImportSelection}
    //   items={importDropdownItems}
    //   disabled={viewMode || !actionPermissionAllowed}
    // >
    //   Import
    // </SelectDropDown>
    <ButtonComponent
      buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
      disabled={viewMode || !actionPermissionAllowed || isGridInEditMode}
      onClick={() => setImportFromCostDatabase(true)}
    >
      <Text>{t("Import from Cost database")}</Text>
    </ButtonComponent>
  );

  const calculateSay = (rateAnalysisData: RateAnalysisData, unitCount?: number) => {
    unitCount = unitCount ?? purchaseUnitCountRef.current;
    return rateAnalysisData.total / unitCount;

    // required if need to round off say amount
    // const roundedSayAmount = Math.floor(rateAnalysisData.total / unitCount);
    // const decimalValue = Math.floor((rateAnalysisData.total / unitCount - roundedSayAmount) * 1000);
    // let correctedValue;
    // if (decimalValue % 100 >= 25 && decimalValue % 100 < 75) {
    //   correctedValue = Math.floor(decimalValue / 100) * 10 + 5;
    // } else if (decimalValue % 100 < 25) {
    //   correctedValue = Math.floor(decimalValue / 100) * 10;
    // } else {
    //   correctedValue = (Math.floor(decimalValue / 100) + 1) * 10;
    // }

    // return roundedSayAmount + correctedValue / 100;
  };

  return (
    <div className="flex flex-col overflow-auto w-full">
      {openAttachmentModal && (
        <Attachments
          closeAttachmentModal={closeAttachmentModal}
          viewMode={viewMode}
          attachments={openAttachmentModal.attachments || []}
          handleAttachFiles={handleAttachFiles}
        />
      )}
      {!showAnalysisOnly && (
        <div className="flex justify-between pt-1 pb-2">
          <Text className="text-lg font-semibold">{t("Item Details")}</Text>
          {importButton()}
        </div>
      )}

      {!showAnalysisOnly && (
        <div className="text-sm">
          {parentRecord && (
            <div className="pt-2 flex gap-4 items-center">
              {parentRecord.code}
              <span>{parentRecord.description}</span>
            </div>
          )}
          <div className={`pt-2 ${parentRecord && "ps-2"} flex gap-4 items-center`}>
            {checkedRecord?.code}
            <span>{checkedRecord?.description}</span>
          </div>
        </div>
      )}

      {!showAnalysisOnly && <Text className="py-4 text-lg font-semibold flex items-center">{t("Analysis")}</Text>}
      <div className="flex justify-between pb-1">
        <div className="flex items-center text-sm gap-4 ">
          <Text>{t("Details for Cost of")}</Text>
          <div className="w-32">
            {numericComponent({
              name: "purchaseUnit",
              value: purchaseUnitCount,
              min: 1,
            })}
          </div>
          <span>{purchaseUnitName}</span>
        </div>
        {showAnalysisOnly && importButton()}
      </div>
      <div className="h-80 pb-6 w-full">{chargesItems && rateAnalysisGrid()}</div>
      <div className="h-8 items-center ps-4 flex justify-end gap-2 text-sm mb-2">
        <div className="flex w-36 gap-2">
          <Text className="flex-1.5">{t("Cost of")}</Text>
          <span className="flex-1 overflow-hidden text-ellipsis text-nowrap">{purchaseUnitCount}</span>
          <span className="flex-2 overflow-hidden text-ellipsis text-nowrap">{purchaseUnitName}</span>
        </div>
        <div className="flex items-center font-bold w-32 ps-4  text-primary_text bg-surface_neutral h-full">
          {repharseNumberField(rateAnalysisData.total, "amount")}
        </div>
      </div>
      <div className="h-8 ps-4 items-center flex justify-end gap-2 text-sm mb-2">
        <div className="flex gap-2 w-36">
          <Text className="flex-1.5">{t("Cost of")}</Text>
          <span className="flex-1">1</span>
          <span className="flex-2 overflow-hidden text-ellipsis text-nowrap">{purchaseUnitName}</span>
        </div>
        <div className="flex items-center w-32 font-bold ps-4 text-primary_text bg-surface_neutral h-full">
          {repharseNumberField(rateAnalysisData.total / purchaseUnitCount, "amount")}
        </div>
      </div>
      <div className="h-8 ps-4 items-center mb-4 flex justify-end gap-2 text-sm">
        <div className="w-36 h-full items-center flex justify-between">
          <Text>{t("Say")}</Text>
          {!viewMode &&
            parseFloat(rateAnalysisData.say.toFixed(2)) !==
              parseFloat((rateAnalysisData.total / purchaseUnitCount).toFixed(2)) && (
              <IconComponent
                className={actionPermissionAllowed ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}
                name="refresh"
                width="1.5rem"
                height="1.5rem"
                onClick={refreshSayAmount}
              />
            )}
        </div>
        <div className="w-32">
          {numericComponent({
            name: "say",
            value: rateAnalysisData.say,
            step: 0.01,
          })}
        </div>
      </div>
      {costDatabaseModal()}
      {confirmImportModal()}
      {!showAnalysisOnly && (
        <div className="flex gap-4 mb-4">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            disabled={viewMode || !isUserPerformedAction || !actionPermissionAllowed || isGridInEditMode}
            onClick={handleSaveClickHandler}
          >
            <Text>{t("Add")}</Text>
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleCloseRateAnalysisModal}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      )}

      {/* {openSubAOR.show && <SubAOR data = {openSubAOR.data} handleCloseSubAor={handleCloseSubAOR} handleSaveSubAOR ={handleSaveSubAOR} costDatabaseCurrencySymbol ={costDatabaseCurrencySymbol} source={source}/>} */}
    </div>
  );
}
RateAnalysis.displayName = "RateAnalysis";
export default RateAnalysis;
